import { ChangeEvent, useEffect, useRef, useState } from 'react';
import _ from 'lodash';

import IconAddPlus from '../../../icons/IconAddPlus.svg';
import { HelpBadge } from '../../../shared/HelpBadge';
import { Icon } from '../../../shared/Icon';
import Loading from '../../../shared/Loading';
import {
    STOREFRONT_STATUS_TOOLTIP,
    StorefrontStatusText,
} from '../../../shared/StorefrontStatusBadge';
import { PriceIdDeleteButton } from '../PriceIdDeleteButton';
import { PriceIdExpandButton } from '../PriceIdExpandButton';
import { PackPriceIdGenerator } from './PackPriceIdGenerator/PackPriceIdGenerator';
import { LabelInput, Modal, SpeechBubble } from '../../../shared';
import { UserRoles } from '@lifeserver/shared';

import {
    useIapProducts,
    useSyncIapProductStatuses,
} from '../../../hooks/iapProducts';
import { usePriceLevelsFromGoogleSheet } from '../../../hooks/packs';
import { useIsAuthorized } from '../../../hooks/useIsAuthorized';

import {
    addSkuNamePrefix,
    getNameSuggestionFromId,
    hasSkuNamePrefix,
    isPackBundle,
    removeSkuNamePrefix,
} from '../../utils';

import { Store } from '../../../types/enums/store.enum';

import { Pack, PlatformData, PriceLevel } from '../../../types/packTypes';

import styles from './PackPriceId.module.scss';

import { emptyPriceLevel } from '../../emptyPack';

enum PossibleInputFieldsNames {
    Min = 'min',
    Max = 'max',
    Price = 'price',
}

type PackPriceIdProps = {
    pack: Pack;
    childPacks: Pack[];
    onPriceLevelsChange: (pls: PriceLevel[]) => void;
};

export function PackPriceId(props: PackPriceIdProps) {
    const { pack, childPacks, onPriceLevelsChange } = props;
    const [expandedPrice, setExpandedPrice] = useState<number | null>(null);
    const priceInput = useRef<HTMLInputElement>(null);
    const {
        priceLevels,
        load: loadGoogleSheetData,
        isLoading,
        error: errorMessage,
    } = usePriceLevelsFromGoogleSheet(pack.packId);

    // Read-only view of the storefront statuses maintained by the SKU
    // management page, keyed so each platform row can look its own up.
    const { products } = useIapProducts();
    const storefrontStatusByStoreSku = new Map(
        (products ?? []).map((product) => [
            `${product.store}:${product.sku}`,
            product.storefrontStatus,
        ]),
    );

    const { isAuthorized } = useIsAuthorized(UserRoles.PriceAutomationWrite);
    const {
        syncStatusesAsync,
        isSyncing,
        errorMessage: syncErrorMessage,
    } = useSyncIapProductStatuses();
    const [syncingKey, setSyncingKey] = useState<string | null>(null);
    const [copiedKey, setCopiedKey] = useState<string | null>(null);

    const copySku = async (store: PlatformData['store'], sku: string) => {
        try {
            await navigator.clipboard.writeText(sku);
            setCopiedKey(`${store}:${sku}`);
            setTimeout(() => setCopiedKey(null), 1500);
        } catch {
            // Clipboard access can be denied; a UI error would be noisier
            // than the convenience is worth (same call as CategoryModal).
        }
    };

    const isSyncableStore = (store: PlatformData['store']) =>
        store === Store.APPLE || store === Store.GOOGLE;

    const STOREFRONT_NAMES: Partial<Record<PlatformData['store'], string>> = {
        [Store.APPLE]: 'App Store',
        [Store.GOOGLE]: 'Google Play',
    };

    const syncPlatformSku = async (
        store: PlatformData['store'],
        sku: string,
    ) => {
        setSyncingKey(`${store}:${sku}`);
        try {
            await syncStatusesAsync({ skus: [sku], store });
        } catch {
            // The failure is surfaced through syncErrorMessage below.
        } finally {
            setSyncingKey(null);
        }
    };

    useEffect(() => {
        const lastIndex = pack.priceLevels.length - 1;

        if (lastIndex < 0) {
            return;
        }

        // If new price level is added
        if (!pack.priceLevels[lastIndex].productId) {
            setExpandedPrice(lastIndex);
        }

        priceInput.current?.focus();
    }, [pack.priceLevels.length]);

    useEffect(() => {
        if (!priceLevels.length) {
            return;
        }

        onPriceLevelsChange(pack.priceLevels.concat(priceLevels));
    }, [priceLevels]);

    const onPriceLevelSkuChange = (
        e: ChangeEvent<HTMLInputElement>,
        priceLevelIndex: number,
    ) => {
        const product = pack.priceLevels[priceLevelIndex];
        const newValue = hasSkuNamePrefix(product.productId)
            ? addSkuNamePrefix(e.target.value)
            : e.target.value;

        const platformData = product.platformData.map((pd) => ({
            ...pd,
            sku: product.productId === pd.sku ? newValue : pd.sku,
        }));

        onPriceLevelsChange(
            _.set([...pack.priceLevels], priceLevelIndex, {
                ...product,
                productId: newValue,
                platformData,
            }),
        );
    };

    const onInputFieldChange = (
        e: ChangeEvent<HTMLInputElement>,
        priceLevelIndex: number,
    ) => {
        const newValue = e.target.value;
        const name = e.target.name as PossibleInputFieldsNames;
        const product = pack.priceLevels[priceLevelIndex];

        onPriceLevelsChange(
            _.set([...pack.priceLevels], priceLevelIndex, {
                ...product,
                [name]:
                    // Set empty price to null so that js doesn't turn '' into 0
                    name === PossibleInputFieldsNames.Price && newValue === ''
                        ? null
                        : Number(newValue),
            }),
        );
    };

    const onPlatformDataChange = (
        e: ChangeEvent<HTMLInputElement>,
        priceLevelIndex: number,
        platformDataIndex: number,
    ) => {
        const name = e.target.name;

        let value;
        if (e.target.type === 'checkbox') {
            value = e.target.checked;
        } else {
            const previousName = _.get(pack.priceLevels, [
                priceLevelIndex,
                'platformData',
                platformDataIndex,
                name,
            ]);
            if (hasSkuNamePrefix(previousName)) {
                value = addSkuNamePrefix(e.target.value);
            } else {
                value = e.target.value;
            }
        }

        onPriceLevelsChange(
            _.set(
                _.cloneDeep(pack.priceLevels),
                [priceLevelIndex, 'platformData', platformDataIndex, name],
                value,
            ),
        );
    };

    const deletePriceId = async (index: number) => {
        const isConfirmed = await Modal.confirm({
            heading: 'Delete product',
            text: 'Really delete this product?',
            okLabel: 'Yes do it!',
        });

        if (!isConfirmed) return;

        onPriceLevelsChange(
            _.without(pack.priceLevels, pack.priceLevels[index]),
        );
    };

    const onNewPriceIdAdded = () => {
        const priceLevel = _.cloneDeep(
            emptyPriceLevel,
        ) as unknown as PriceLevel;
        const packName = getNameSuggestionFromId(pack.packId);
        priceLevel.productId = addSkuNamePrefix(packName);

        for (const platform of priceLevel.platformData) {
            platform.sku = priceLevel.productId;
        }

        onPriceLevelsChange(_.concat(pack.priceLevels, [priceLevel]));
    };

    const addXsollaPlatform = (priceLevelIndex: number) => {
        const priceLevels = _.cloneDeep(pack.priceLevels);
        const priceLevel = priceLevels[priceLevelIndex];

        const xsollaPlatform = {
            store: Store.XSOLLA,
            sku: priceLevel.productId,
            active: true,
            deprecatedSkus: [],
        } as const satisfies PlatformData;
        priceLevel.platformData.push(xsollaPlatform);

        onPriceLevelsChange(priceLevels);
    };

    if (isLoading) {
        return <Loading style={{ textAlign: 'center' }} />;
    }

    const getPriceLevelNameInputClass = (priceLevel: PriceLevel): string => {
        return `labelInput ${hasSkuNamePrefix(priceLevel.productId) ? 'shortwide' : 'semiwide'}`;
    };

    const getPlatformDataNameInputClass = (name: string): string => {
        return `labelInput prices ${hasSkuNamePrefix(name) ? 'shortwide' : 'semiwide'}`;
    };

    const onSKUsGenerated = async (newPriceLevels: PriceLevel[]) => {
        let canGenerate = true;
        if (pack.priceLevels.length > 0) {
            canGenerate = await Modal.confirm({
                heading: `This bundle already have SKUs`,
                text: `This action will remove all existing SKUs and generate new ones. Do you want to continue?`,
            });
        }

        if (canGenerate) {
            onPriceLevelsChange(newPriceLevels);
        }
    };

    return (
        <>
            {isPackBundle(pack) ? (
                <PackPriceIdGenerator
                    pack={pack}
                    childPacks={childPacks}
                    onSKUsGenerated={onSKUsGenerated}
                />
            ) : (
                <>
                    <h4>Enter SKU names below.</h4>
                    <SpeechBubble
                        style={{
                            justifyContent: 'left',
                            marginTop: 10,
                            marginBottom: 20,
                        }}
                    >
                        Set Min/Max to -1 for all packs except bundles. For
                        strikethrough price set min/max to 1000.
                    </SpeechBubble>
                </>
            )}
            {pack.priceLevels.map((price, priceIndex) => {
                const isOpen = expandedPrice === priceIndex;
                const hasXsollaPlatformData = price.platformData.some(
                    (pd) => pd.store === Store.XSOLLA,
                );

                return (
                    <div key={`price${priceIndex}`} style={{ marginBottom: 5 }}>
                        <PriceIdExpandButton
                            isOpen={isOpen}
                            onClick={() => {
                                setExpandedPrice(isOpen ? null : priceIndex);
                            }}
                        />
                        <div className="labelInput">
                            {' '}
                            {/* skip LabelInput component to make focus field on new easier */}
                            {priceIndex === 0 && (
                                <label htmlFor={`product-id-${priceIndex}`}>
                                    Product ID/SKU
                                </label>
                            )}
                            {hasSkuNamePrefix(
                                pack.priceLevels[priceIndex].productId,
                            ) && <span>com.tocaboca.tocalifeworld.</span>}
                            <input
                                id={`product-id-${priceIndex}`}
                                className={getPriceLevelNameInputClass(
                                    pack.priceLevels[priceIndex],
                                )}
                                style={{ marginBottom: 0 }}
                                value={removeSkuNamePrefix(
                                    pack.priceLevels[priceIndex].productId,
                                )}
                                ref={priceInput}
                                onChange={(e) =>
                                    onPriceLevelSkuChange(e, priceIndex)
                                }
                            ></input>
                        </div>
                        <LabelInput
                            className="prices"
                            size="tiny"
                            type="number"
                            tooltip="Min unowned locations to get this price."
                            name={PossibleInputFieldsNames.Min}
                            label={priceIndex === 0 ? 'min' : ''}
                            value={price.min}
                            onChange={(e) => onInputFieldChange(e, priceIndex)}
                        />
                        <LabelInput
                            className="prices"
                            size="tiny"
                            type="number"
                            tooltip="Max unowned locations to get this price."
                            name={PossibleInputFieldsNames.Max}
                            value={price.max}
                            label={priceIndex === 0 ? 'max' : ''}
                            onChange={(e) => onInputFieldChange(e, priceIndex)}
                        />
                        <LabelInput
                            className="prices"
                            size="tiny"
                            type="number"
                            inputMode="decimal"
                            tooltip="Price for the store front export."
                            name={PossibleInputFieldsNames.Price}
                            value={price.price}
                            label={priceIndex === 0 ? 'Price' : ''}
                            onChange={(e) => onInputFieldChange(e, priceIndex)}
                        />
                        <PriceIdDeleteButton
                            onClick={() => {
                                deletePriceId(priceIndex);
                            }}
                        />

                        <table
                            className={styles.platformDataTable}
                            style={{ display: isOpen ? 'block' : 'none' }}
                        >
                            <thead>
                                <tr>
                                    <th>Active</th>
                                    <th style={{ width: 87 }}>Platform</th>
                                    <th>Sku</th>
                                    <th style={{ paddingRight: 32 }}>
                                        Status
                                        <HelpBadge
                                            title={STOREFRONT_STATUS_TOOLTIP}
                                            ariaLabel="Status help"
                                        />
                                    </th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {price.platformData.map(
                                    (platformData, platformDataIndex) => {
                                        return (
                                            <tr
                                                className="platformData"
                                                key={`platformData${priceIndex}_${platformDataIndex}`}
                                            >
                                                <td>
                                                    <input
                                                        className="labelInput prices tiny"
                                                        type="checkbox"
                                                        checked={
                                                            platformData.active
                                                        }
                                                        name="active"
                                                        onChange={(event) => {
                                                            onPlatformDataChange(
                                                                event,
                                                                priceIndex,
                                                                platformDataIndex,
                                                            );
                                                        }}
                                                    />
                                                </td>
                                                <td
                                                    style={{
                                                        fontFamily: 'standard',
                                                    }}
                                                >
                                                    {platformData.store}
                                                </td>
                                                <td>
                                                    {hasSkuNamePrefix(
                                                        platformData.sku,
                                                    ) && (
                                                        <span>
                                                            com.tocaboca.tocalifeworld.
                                                        </span>
                                                    )}
                                                    <input
                                                        className={getPlatformDataNameInputClass(
                                                            platformData.sku,
                                                        )}
                                                        type="text"
                                                        value={removeSkuNamePrefix(
                                                            platformData.sku,
                                                        )}
                                                        name="sku"
                                                        onChange={(event) => {
                                                            onPlatformDataChange(
                                                                event,
                                                                priceIndex,
                                                                platformDataIndex,
                                                            );
                                                        }}
                                                    />
                                                </td>
                                                <td>
                                                    {isSyncableStore(
                                                        platformData.store,
                                                    ) ? (
                                                        <StorefrontStatusText
                                                            status={storefrontStatusByStoreSku.get(
                                                                `${platformData.store}:${platformData.sku}`,
                                                            )}
                                                        />
                                                    ) : (
                                                        '—'
                                                    )}
                                                </td>
                                                <td
                                                    style={{
                                                        verticalAlign: 'middle',
                                                    }}
                                                >
                                                    <button
                                                        className="small"
                                                        style={{
                                                            marginRight: 10,
                                                        }}
                                                        title={
                                                            copiedKey ===
                                                            `${platformData.store}:${platformData.sku}`
                                                                ? 'Copied!'
                                                                : 'Copy full SKU'
                                                        }
                                                        aria-label={`Copy full SKU ${platformData.sku}`}
                                                        onClick={(e) => {
                                                            e.preventDefault();
                                                            void copySku(
                                                                platformData.store,
                                                                platformData.sku,
                                                            );
                                                        }}
                                                    >
                                                        <Icon
                                                            name={
                                                                copiedKey ===
                                                                `${platformData.store}:${platformData.sku}`
                                                                    ? 'check'
                                                                    : 'content_copy'
                                                            }
                                                        />
                                                    </button>
                                                    {isSyncableStore(
                                                        platformData.store,
                                                    ) &&
                                                        isAuthorized && (
                                                            <button
                                                                className="small"
                                                                disabled={
                                                                    isSyncing &&
                                                                    syncingKey ===
                                                                        `${platformData.store}:${platformData.sku}`
                                                                }
                                                                onClick={(
                                                                    e,
                                                                ) => {
                                                                    e.preventDefault();
                                                                    void syncPlatformSku(
                                                                        platformData.store,
                                                                        platformData.sku,
                                                                    );
                                                                }}
                                                            >
                                                                {isSyncing &&
                                                                syncingKey ===
                                                                    `${platformData.store}:${platformData.sku}`
                                                                    ? 'Syncing...'
                                                                    : `Sync with ${STOREFRONT_NAMES[platformData.store]}`}
                                                            </button>
                                                        )}
                                                </td>
                                            </tr>
                                        );
                                    },
                                )}
                                {!hasXsollaPlatformData && (
                                    <tr
                                        className={`platformData ${styles.addXsollaRow}`}
                                    >
                                        <td colSpan={5}>
                                            <button
                                                className={`small primary ${styles.addXsollaButton}`}
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    addXsollaPlatform(
                                                        priceIndex,
                                                    );
                                                }}
                                            >
                                                <img
                                                    src={IconAddPlus}
                                                    alt="Add Xsolla"
                                                />
                                                Add Xsolla
                                            </button>
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                );
            })}
            <div className={styles.buttonsBlock}>
                <button onClick={onNewPriceIdAdded}>Add new</button>
                <button onClick={loadGoogleSheetData}>
                    Import from Spreadsheet
                </button>
                <div className={styles.error}>
                    {errorMessage || syncErrorMessage || ''}
                </div>
            </div>
        </>
    );
}
